import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  RefreshCw, Link, Link2, Upload, CheckCircle, XCircle,
  Clock, KeyRound, X, Trash2,
} from 'lucide-react';
import { base44 } from '../api/apiClient';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const PROVIDERS = [
  {
    key: 'sumup',
    name: 'SumUp',
    description: 'Sync sales via OAuth 2.0. Transactions are imported automatically.',
    supportsSync: true,
    supportsMenu: false,
    supportsCSV: false,
  },
  {
    key: 'lightspeed',
    name: 'Lightspeed Restaurant',
    description: 'Sync sales and menu items via OAuth 2.0.',
    supportsSync: true,
    supportsMenu: true,
    supportsCSV: false,
  },
  {
    key: 'orderbird',
    name: 'Orderbird',
    description: null,
    supportsSync: false,
    supportsMenu: false,
    supportsCSV: true,
  },
];

const STATUS_STYLES = {
  success: 'text-green-500',
  error: 'text-red-500',
  running: 'text-yellow-500',
  pending: 'text-slate-400',
};

const StatusIcon = ({ status }) => {
  if (status === 'success') return <CheckCircle className="w-4 h-4 text-green-500" />;
  if (status === 'error') return <XCircle className="w-4 h-4 text-red-500" />;
  return <Clock className="w-4 h-4 text-yellow-500" />;
};

export default function POSIntegrationsSection() {
  const { t } = useTranslation();
  const fileInputRef = useRef(null);

  const [integrations, setIntegrations] = useState([]);
  const [syncLogs, setSyncLogs] = useState({});
  const [syncing, setSyncing] = useState({});
  const [loadingLogs, setLoadingLogs] = useState({});
  const [csvUploading, setCsvUploading] = useState(false);
  const [pendingCSVId, setPendingCSVId] = useState(null);
  const [uploadResult, setUploadResult] = useState(null); // {status, records_synced, error_message}

  // Credentials modal
  const [credentialsModal, setCredentialsModal] = useState(null);
  const [credClientId, setCredClientId] = useState('');
  const [credClientSecret, setCredClientSecret] = useState('');
  const [credSaving, setCredSaving] = useState(false);
  const [credError, setCredError] = useState('');

  useEffect(() => {
    loadIntegrations();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle ?connected= or ?error= redirected back from OAuth
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('connected') || params.get('error')) {
      loadIntegrations();
      window.history.replaceState({}, '', window.location.pathname);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadIntegrations = async () => {
    const data = await base44.pos.getIntegrations();
    setIntegrations(data);
  };

  const getIntegration = (providerKey) =>
    integrations.find((i) => i.provider === providerKey) || null;

  const handleConnect = (providerKey) => {
    const token = localStorage.getItem('access_token');
    if (providerKey === 'sumup') {
      window.location.href = `${API_URL}/pos/oauth/sumup/authorize/?token=${token}`;
    } else if (providerKey === 'lightspeed') {
      window.location.href = `${API_URL}/pos/oauth/lightspeed/authorize/?token=${token}`;
    }
  };

  const handleDisconnect = async (integration) => {
    if (!window.confirm(t('pos.confirmDisconnect', { provider: integration.provider }))) return;
    await base44.pos.disconnect(integration.id);
    await loadIntegrations();
  };

  const handleSync = async (integration, syncType = 'sales') => {
    const key = `${integration.id}-${syncType}`;
    setSyncing((prev) => ({ ...prev, [key]: true }));
    try {
      const log = syncType === 'menu'
        ? await base44.pos.syncMenu(integration.id)
        : await base44.pos.syncSales(integration.id);
      setSyncLogs((prev) => ({ ...prev, [integration.id]: [log, ...(prev[integration.id] || [])] }));
      await loadIntegrations();
    } catch (err) {
      console.error('Sync error:', err);
    } finally {
      setSyncing((prev) => ({ ...prev, [key]: false }));
    }
  };

  const loadSyncLogs = async (integrationId) => {
    setLoadingLogs((prev) => ({ ...prev, [integrationId]: true }));
    try {
      const data = await base44.pos.getLogs(integrationId);
      setSyncLogs((prev) => ({ ...prev, [integrationId]: data }));
    } catch {
      // ignore
    } finally {
      setLoadingLogs((prev) => ({ ...prev, [integrationId]: false }));
    }
  };

  const toggleLogs = (integrationId) => {
    if (syncLogs[integrationId] !== undefined) {
      setSyncLogs((prev) => { const n = { ...prev }; delete n[integrationId]; return n; });
    } else {
      loadSyncLogs(integrationId);
    }
  };

  const handleCSVUpload = async (integrationId, file) => {
    setCsvUploading(true);
    setUploadResult(null);
    try {
      const log = await base44.pos.uploadOrderbirdCSV(integrationId, file);
      setSyncLogs((prev) => ({ ...prev, [integrationId]: [log, ...(prev[integrationId] || [])] }));
      setUploadResult({ status: log.status, records_synced: log.records_synced, error_message: log.error_message });
      await loadIntegrations();
    } catch (err) {
      console.error('CSV upload error:', err);
      setUploadResult({ status: 'error', error_message: err?.response?.data?.error || 'Upload failed.' });
    } finally {
      setCsvUploading(false);
      setPendingCSVId(null);
    }
  };

  const triggerFileInput = async (integrationId, providerKey) => {
    let id = integrationId;
    if (!id) {
      try {
        const created = await base44.pos.createIntegration(providerKey);
        id = created.id;
        await loadIntegrations();
      } catch (err) {
        console.error('Failed to create integration:', err);
        return;
      }
    }
    setPendingCSVId(id);
    fileInputRef.current?.click();
  };

  const onFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file && pendingCSVId) handleCSVUpload(pendingCSVId, file);
    e.target.value = '';
  };

  const openCredentialsModal = (providerKey, providerName) => {
    setCredClientId('');
    setCredClientSecret('');
    setCredError('');
    setCredentialsModal({ providerKey, providerName });
  };

  const closeCredentialsModal = () => setCredentialsModal(null);

  const handleSaveCredentials = async () => {
    if (!credClientId.trim() || !credClientSecret.trim()) {
      setCredError('Both fields are required.');
      return;
    }
    setCredSaving(true);
    setCredError('');
    try {
      await base44.pos.saveCredentials(credentialsModal.providerKey, credClientId.trim(), credClientSecret.trim());
      await loadIntegrations();
      closeCredentialsModal();
    } catch {
      setCredError('Failed to save credentials. Please try again.');
    } finally {
      setCredSaving(false);
    }
  };

  const handleDeleteCredentials = async (providerKey, providerName) => {
    if (!window.confirm(`Are you sure you want to delete the credentials for ${providerName}? This will also disconnect the integration.`)) return;
    try {
      await base44.pos.deleteCredentials(providerKey);
      await loadIntegrations();
    } catch {
      // ignore
    }
  };

  const formatDate = (iso) => {
    if (!iso) return t('pos.neverSynced');
    return new Date(iso).toLocaleString();
  };

  return (
    <>
      <div className="space-y-4">
        {PROVIDERS.map((provider) => {
          const conn = getIntegration(provider.key);
          const isActive = conn?.is_active;
          const logs = syncLogs[conn?.id] || [];

          return (
            <div key={provider.key} className="bg-white rounded-xl border border-slate-200 shadow-sm">
              <div className="p-5 flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h2 className="text-slate-900 font-semibold">{provider.name}</h2>
                    {isActive ? (
                      <span className="flex items-center gap-1 text-xs text-green-600 bg-green-50 px-2 py-0.5 rounded-full border border-green-200">
                        <CheckCircle className="w-3 h-3" /> {t('pos.connected')}
                      </span>
                    ) : (
                      <span className="text-xs text-slate-400 bg-slate-100 px-2 py-0.5 rounded-full">
                        {t('pos.notConnected')}
                      </span>
                    )}
                  </div>
                  <p className="text-slate-500 text-sm">
                    {provider.description || t('pos.orderbirdNote')}
                  </p>
                  {conn?.last_synced_at && (
                    <p className="text-slate-400 text-xs mt-1">
                      {t('pos.lastSync')}: {formatDate(conn.last_synced_at)}
                    </p>
                  )}
                </div>

                <div className="flex items-center gap-2 flex-shrink-0">
                  {!provider.supportsCSV && (
                    isActive ? (
                      <button
                        onClick={() => handleDisconnect(conn)}
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-red-50 hover:bg-red-100 text-red-600 border border-red-200 rounded-lg text-xs transition-colors"
                      >
                        <Link2 className="w-3.5 h-3.5" />
                        {t('pos.disconnect')}
                      </button>
                    ) : (
                      <>
                        <button
                          onClick={() => openCredentialsModal(provider.key, provider.name)}
                          className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-50 hover:bg-slate-100 text-slate-600 border border-slate-200 rounded-lg text-xs transition-colors"
                        >
                          <KeyRound className="w-3.5 h-3.5" />
                          {conn?.has_credentials ? 'Credentials saved' : 'Set credentials'}
                        </button>
                        {conn?.has_credentials && (
                          <button
                            onClick={() => handleDeleteCredentials(provider.key, provider.name)}
                            className="flex items-center gap-1.5 px-3 py-1.5 bg-red-50 hover:bg-red-100 text-red-600 border border-red-200 rounded-lg text-xs transition-colors"
                            title="Delete credentials"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                            Delete credentials
                          </button>
                        )}
                        <button
                          onClick={() => handleConnect(provider.key)}
                          disabled={!conn?.has_credentials}
                          title={!conn?.has_credentials ? 'Set credentials first' : ''}
                          className="flex items-center gap-1.5 px-3 py-1.5 bg-green-600 hover:bg-green-500 text-white rounded-lg text-xs transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                          <Link className="w-3.5 h-3.5" />
                          {t('pos.connect')}
                        </button>
                      </>
                    )
                  )}

                  {provider.supportsSync && isActive && (() => {
                    const key = `${conn.id}-sales`;
                    const isSyncing = syncing[key];
                    return (
                      <button
                        onClick={() => handleSync(conn, 'sales')}
                        disabled={isSyncing}
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 hover:bg-blue-100 text-blue-600 border border-blue-200 rounded-lg text-xs transition-colors disabled:opacity-50"
                      >
                        <RefreshCw className={`w-3.5 h-3.5 ${isSyncing ? 'animate-spin' : ''}`} />
                        {isSyncing ? t('pos.syncing') : t('pos.syncSales')}
                      </button>
                    );
                  })()}

                  {provider.supportsMenu && isActive && (() => {
                    const key = `${conn.id}-menu`;
                    const isSyncing = syncing[key];
                    return (
                      <button
                        onClick={() => handleSync(conn, 'menu')}
                        disabled={isSyncing}
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-purple-50 hover:bg-purple-100 text-purple-600 border border-purple-200 rounded-lg text-xs transition-colors disabled:opacity-50"
                      >
                        <RefreshCw className={`w-3.5 h-3.5 ${isSyncing ? 'animate-spin' : ''}`} />
                        {isSyncing ? t('pos.syncing') : t('pos.syncMenu')}
                      </button>
                    );
                  })()}

                  {provider.supportsCSV && (() => (
                    <button
                      onClick={() => triggerFileInput(conn?.id, provider.key)}
                      disabled={csvUploading}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-orange-50 hover:bg-orange-100 text-orange-600 border border-orange-200 rounded-lg text-xs transition-colors disabled:opacity-50"
                    >
                      <Upload className="w-3.5 h-3.5" />
                      {csvUploading ? t('pos.uploading') : t('pos.uploadCSV')}
                    </button>
                  ))()}

                  {conn && (
                    <button
                      onClick={() => toggleLogs(conn.id)}
                      className="px-2.5 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-500 rounded-lg text-xs transition-colors"
                    >
                      {syncLogs[conn.id] !== undefined ? t('pos.hideLogs') : t('pos.syncHistory')}
                    </button>
                  )}
                </div>
              </div>

              {conn && syncLogs[conn.id] !== undefined && (
                <div className="border-t border-slate-100 px-5 pb-4">
                  <p className="text-slate-400 text-xs font-semibold uppercase tracking-wide mt-3 mb-2">
                    {t('pos.syncHistory')}
                  </p>
                  {loadingLogs[conn.id] ? (
                    <p className="text-slate-400 text-xs">Loading...</p>
                  ) : logs.length === 0 ? (
                    <p className="text-slate-400 text-xs">{t('pos.noLogs')}</p>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="text-slate-400 border-b border-slate-100">
                            <th className="text-left py-1.5 pr-4">Type</th>
                            <th className="text-left py-1.5 pr-4">Status</th>
                            <th className="text-right py-1.5 pr-4">Records</th>
                            <th className="text-left py-1.5 pr-4">Started</th>
                            <th className="text-left py-1.5">Error</th>
                          </tr>
                        </thead>
                        <tbody>
                          {logs.slice(0, 10).map((log) => (
                            <tr key={log.id} className="border-b border-slate-50">
                              <td className="py-1.5 pr-4 text-slate-700 capitalize">{log.sync_type}</td>
                              <td className="py-1.5 pr-4">
                                <div className="flex items-center gap-1">
                                  <StatusIcon status={log.status} />
                                  <span className={STATUS_STYLES[log.status] || 'text-slate-400'}>
                                    {log.status}
                                  </span>
                                </div>
                              </td>
                              <td className="py-1.5 pr-4 text-right text-slate-700">{log.records_synced}</td>
                              <td className="py-1.5 pr-4 text-slate-400">
                                {log.started_at ? new Date(log.started_at).toLocaleString() : '—'}
                              </td>
                              <td className="py-1.5 text-red-400 truncate max-w-xs">
                                {log.error_message || '—'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {uploadResult && (
        <div className={`mt-3 p-3 rounded-lg text-sm flex items-start justify-between gap-3 ${
          uploadResult.status === 'success'
            ? 'bg-green-50 text-green-700 border border-green-200'
            : 'bg-red-50 text-red-700 border border-red-200'
        }`}>
          <span>
            {uploadResult.status === 'success'
              ? `Upload successful — ${uploadResult.records_synced} record(s) imported.`
              : `Upload failed: ${uploadResult.error_message || 'Unknown error'}`}
          </span>
          <button onClick={() => setUploadResult(null)} className="text-current opacity-60 hover:opacity-100 flex-shrink-0">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept=".csv"
        className="hidden"
        onChange={onFileChange}
      />

      {/* Credentials Modal */}
      {credentialsModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4 p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <KeyRound className="w-5 h-5 text-slate-600" />
                <h2 className="text-slate-900 font-semibold">{credentialsModal.providerName} — OAuth Credentials</h2>
              </div>
              <button onClick={closeCredentialsModal} className="text-slate-400 hover:text-slate-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <p className="text-slate-500 text-sm mb-4">
              Enter the Client ID and Client Secret from your {credentialsModal.providerName} developer app. These are stored encrypted in the database.
            </p>
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Client ID</label>
                <input
                  type="text"
                  value={credClientId}
                  onChange={(e) => setCredClientId(e.target.value)}
                  placeholder="e.g. YOUR_CLIENT_ID"
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Client Secret</label>
                <input
                  type="password"
                  value={credClientSecret}
                  onChange={(e) => setCredClientSecret(e.target.value)}
                  placeholder="••••••••"
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
            {credError && <p className="mt-3 text-red-500 text-xs">{credError}</p>}
            <div className="flex justify-end gap-2 mt-5">
              <button
                onClick={closeCredentialsModal}
                className="px-4 py-2 text-sm text-slate-600 hover:text-slate-800"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveCredentials}
                disabled={credSaving}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white text-sm rounded-lg disabled:opacity-50"
              >
                {credSaving ? 'Saving...' : 'Save credentials'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
